type FormValidationType = {
    $forms: NodeListOf<HTMLFormElement>|null,
    init(): void,
    initialisationForm(): void,
};

/**
 * Usage:
 * 1. Ajouter "novalidate" pour empêcher la validation par defaut du formulaire et ajouter la class "formulaire"
 * <form action="" method="" class="formulaire" novalidate>
 * 
 * 2. Construire le formulaire :
 *  Englober dans une class "required" : le label, l'input et le span.error-msg.
 *  Ajouter à la class "required" une class du type d'input : text, email, telephone, select, checkbox.
 *  Dans le span.error-msg ajouter des attribut data-msg-empty pour les messages d'erreurs champs requis si vide.
 *  Pour les champs email et telephone ajouter un attribut pattern aux inputs et un attribut data-msg-pattern sur le span.error-msg
 * 
 * Exemple champs text :
 * <div class="required text">
 *      <label for="field" >label</label>
 *      <input type="text" name="field" id="field">
 *      <span class="error-msg" aria-live="polite" data-msg-empty="Champs requis"></span>     
 * </div>
 * 
 * Exemple champs email :
 * <div class="required email">
 *      <label for="field" >label</label>
 *      <input type="email" name="field" id="field" pattern="^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$">
 *      <span class="error-msg" aria-live="polite" data-msg-empty="Champs requis" data-msg-pattern="Format non valide"></span>     
 * </div>
 * 
 * Lors de la validation en cas d'erreur le message d'erreur sera placé dans le span vide et une class .error sera ajouté sur l'input.
 * 
 */
const FormValidation: FormValidationType = {
    $forms: null,

    init() : void {
        this.$forms = document.querySelectorAll('.formulaire');

        if (this.$forms) {
            this.initialisationForm();
        }
    },

    initialisationForm() : void {
        this.$forms?.forEach($form => {
            const $required = $form.querySelectorAll<HTMLElement>('.required');

            if ($required) {
                $required.forEach($element => {
                    if ($element.classList.contains('email')) {
                        const $email = $element.querySelector<HTMLInputElement>('input[type="email"]');
                        const $error = $element.querySelector<HTMLElement>('.error-msg');
                        $email?.addEventListener('input', () => {
                            // Chaque fois que l'utilisateur saisit quelque chose
                            // on vérifie la validité du champ e-mail.
                            if (!$email.validity.patternMismatch && $email.value !== '') {
                                // S'il y a un message d'erreur affiché et que le champ
                                // est valide, on retire l'erreur
                                if ($error) {
                                    $error.innerHTML = ''; // On réinitialise le contenu
                                }
                                $email.classList.remove('error');
                            }
                        }, false);
                    }

                    if ($element.classList.contains('phone')) {
                        const $phone = $element.querySelector<HTMLInputElement>('input[type="text"]');
                        const $error = $element.querySelector<HTMLElement>('.error-msg');
                        $phone?.addEventListener('input', () => {
                            // Chaque fois que l'utilisateur saisit quelque chose
                            // on vérifie la validité du champ e-mail.
                            if (!$phone.validity.patternMismatch && $phone.value !== '') {
                                // S'il y a un message d'erreur affiché et que le champ
                                // est valide, on retire l'erreur
                                if ($error) {
                                    $error.innerHTML = ''; // On réinitialise le contenu
                                }
                                $phone.classList.remove('error');
                            }
                        }, false);
                    }
                    if ($element.classList.contains('text')) {
                        const $text = $element.querySelector<HTMLInputElement>('input[type="text"]');
                        const $error = $element.querySelector<HTMLElement>('.error-msg');
                        $text?.addEventListener('input', () => {
                            if ($text.value !== '') {
                                if ($error) {
                                    $error.innerHTML = '';
                                }
                                $text.classList.remove('error');
                            }
                        }, false);
                    }
                    if ($element.classList.contains('textarea')) {
                        const $textarea = $element.querySelector<HTMLTextAreaElement>('textarea');
                        const $error = $element.querySelector<HTMLElement>('.error-msg');
                        $textarea?.addEventListener('input', () => {
                            if ($textarea.value !== '') {
                                if ($error) {
                                    $error.innerHTML = '';
                                }
                                $textarea.classList.remove('error');
                            }
                        }, false);
                    }
                    if ($element.classList.contains('select')) {
                        const $select = $element.querySelector<HTMLSelectElement>('select');
                        const $error = $element.querySelector<HTMLElement>('.error-msg');
                        $select?.addEventListener('input', () => {
                            if ($select.value !== '') {
                                if ($error) {
                                    $error.innerHTML = '';
                                }
                                $select.classList.remove('error');
                            }
                        }, false);
                    }
                    if ($element.classList.contains('checkbox')) {
                        const $checkbox = $element.querySelector<HTMLInputElement>('input[type="checkbox"]');
                        const $error = $element.querySelector<HTMLElement>('.error-msg');
                        $checkbox?.addEventListener('input', () => {
                            if ($checkbox.checked === true) {
                                if ($error) {
                                    $error.innerHTML = '';
                                }
                                $checkbox.classList.remove('error');
                            }
                        }, false);
                    }
                });
            }
           
            $form.addEventListener('submit', event => {
                let errorDetect = false;

                if ($required) {
                    $required.forEach($element => {
                        if ($element.classList.contains('email')) {
                            const $email = $element.querySelector<HTMLInputElement>('input[type="email"]');
                            const $error = $element.querySelector<HTMLElement>('.error-msg');
                            if ($email && $email.value === '') {
                                if ($error) {
                                    $error.innerHTML = $error.getAttribute('data-msg-empty') as string;
                                }
                                $email.classList.add('error');
                                errorDetect = true;
                            } else if ($email && $email.validity.patternMismatch) {
                                if ($error) {
                                    $error.innerHTML = $error.getAttribute('data-msg-pattern') as string;
                                }
                                $email.classList.add('error');
                                errorDetect = true;
                            }
                        }
                        if ($element.classList.contains('phone')) {
                            const $phone = $element.querySelector<HTMLInputElement>('input[type="text"]');
                            const $error = $element.querySelector<HTMLElement>('.error-msg');
                            if ($phone && $phone.value === '') {
                                if ($error) {
                                    $error.innerHTML = $error.getAttribute('data-msg-empty') as string;
                                }
                                $phone.classList.add('error');
                                errorDetect = true;
                            } else if ($phone && $phone.validity.patternMismatch) {
                                if ($error) {
                                    $error.innerHTML = $error.getAttribute('data-msg-pattern') as string;
                                }
                                $phone.classList.add('error');
                                errorDetect = true;
                            }
                        }
                        if ($element.classList.contains('text') || $element.classList.contains('textarea')) {
                            const $text = $element.classList.contains('text') ? $element.querySelector<HTMLInputElement>('input[type="text"]') : $element.querySelector<HTMLTextAreaElement>('textarea');
                            const $error = $element.querySelector<HTMLElement>('.error-msg');
                            if ($text && $text.value === '') {
                                if ($error) {
                                    $error.innerHTML = $error.getAttribute('data-msg-empty') as string;
                                }
                                $text.classList.add('error');
                                errorDetect = true;
                            }
                        }
                        if ($element.classList.contains('select')) {
                            const $select = $element.querySelector<HTMLSelectElement>('select');
                            const $error = $element.querySelector<HTMLElement>('.error-msg');
                            if ($select && $select.value === '') {
                                if ($error) {
                                    $error.innerHTML = $error.getAttribute('data-msg-empty') as string;
                                }
                                $select.classList.add('error');
                                errorDetect = true;
                            }
                        }
                        if ($element.classList.contains('checkbox')) {
                            const $checkbox = $element.querySelector<HTMLInputElement>('input[type="checkbox"]');
                            const $error = $element.querySelector<HTMLElement>('.error-msg');
                            if ($checkbox && $checkbox.checked === false) {
                                if ($error) {
                                    $error.innerHTML = $error.getAttribute('data-msg-empty') as string;
                                }
                                $checkbox.classList.add('error');
                                errorDetect = true;
                            }
                        }
                    });
                }

                if (errorDetect) {
                    event.preventDefault();
                }
            }, false);
        });
    },
};

export default FormValidation;
